<div class="header-div d-flex justify-content-center">
  <h6 class="text-uppercase mb-0 text-center">{{ title | translate }}</h6>
</div>

@if (isFormLoading) {
  <app-rings-loader></app-rings-loader>
}

@if (!isFormLoading && stepperConfig) {
  <app-stepper-container [config]="stepperConfig"
    (stepChanged)="onStepChanged($event)">
  </app-stepper-container>
}

<ng-template #raStepTpl>
  <div class="form-fields-body ra-step">

    <!-- ─── STEP 1: BASICS ─────────────────────────────────────────── -->
    @if (currentStep?.id === 'basics') {
      <div class="row">
        @for (field of basicsFields; track trackByIndex($index)) {
          <app-dynamic-form-field [field]="field" [entity]="entity"
          (valueChanged)="updateFieldVisibility()"></app-dynamic-form-field>
          @if (decisionStatisticServerError && field.property === decisionStatisticFieldProperty) {
            <div class="ra-filter-error w-100" role="alert">
              {{ decisionStatisticServerError }}
            </div>
          }
        }
      </div>
    }

    <!-- ─── STEP 2: EXPOSURE LIMITS ────────────────────────────────── -->
    @if (currentStep?.id === 'exposureLimits') {
      <div class="ra-info-banner" role="note">
        <i class="fa fa-info-circle"></i>
        <span>{{ 'The jurisdiction and OEL type you select here set the standard your samples will be scored against. Every statistic (% of OEL, decision statistic, exceedance fraction) uses the OEL from this combination only.' | translate }}</span>
      </div>
      <div class="row">
        @for (field of exposureLimitsFields; track trackByIndex($index)) {
          <app-dynamic-form-field [field]="field" [entity]="entity"
          (valueChanged)="updateFieldVisibility()"></app-dynamic-form-field>
        }
      </div>
      <div class="ra-disclosure">
        <button type="button" class="ra-disclosure-header" [class.open]="fallbackExpanded"
          (click)="toggleFallback()" [disabled]="!isAdd"
          [class.cursor-not-allowed]="!isAdd" [attr.aria-expanded]="fallbackExpanded">
          <span class="ra-disclosure-title">
            <i class="fa fa-sliders"></i>
            {{ 'Include lab results from other sources' | translate }}
          </span>
          <i class="fa" [ngClass]="fallbackExpanded ? 'fa-angle-up' : 'fa-angle-down'"></i>
        </button>
        @if (fallbackExpanded) {
          <div class="ra-disclosure-body" role="region">
            <div class="ra-disclosure-explanation">
              <p>{{ 'These options let you include lab results recorded under a different OEL type or jurisdiction. They do NOT change the OEL your samples are scored against — that always comes from the primary selection above.' | translate }}</p>
              <p class="mt-2">{{ 'Example: if you set Primary OEL Type = TWA90 and also include TWA8 results, the TWA8 samples will appear in the register — but they will still be scored against the TWA90 OEL. If no TWA90 OEL exists for an agent, that agent will show samples but no statistics.' | translate }}</p>
            </div>
            <div class="row">
              @for (field of fallbackFields; track trackByIndex($index)) {
                <app-dynamic-form-field [field]="field" [entity]="entity"
                (valueChanged)="updateFieldVisibility()"></app-dynamic-form-field>
              }
            </div>
          </div>
        }
      </div>
    }

    <!-- ─── STEP 3: STARTING POINT ─────────────────────────────────── -->
    @if (currentStep?.id === 'startingPoint') {
      <p class="ra-step-question">{{ 'How would you like to start this assessment?' | translate }}</p>
      <div class="ra-radio-cards">
        <label class="ra-radio-card" [class.selected]="entity.initialQualitativeSelector === startEmptyValue"
          (click)="selectInitialQualitative(startEmptyValue)">
          <input type="radio" name="raCloneChoice"
            [checked]="entity.initialQualitativeSelector === startEmptyValue">
            <div class="ra-radio-content">
              <div class="ra-radio-label">{{ 'Start empty' | translate }}</div>
              <div class="ra-radio-sub">{{ 'A blank assessment. Registers are created with no qualitative scores, controls, or notes.' | translate }}</div>
            </div>
          </label>
          <label class="ra-radio-card" [class.selected]="entity.initialQualitativeSelector === copyFromValue"
            (click)="selectInitialQualitative(copyFromValue)">
            <input type="radio" name="raCloneChoice"
              [checked]="entity.initialQualitativeSelector === copyFromValue">
              <div class="ra-radio-content">
                <div class="ra-radio-label">{{ 'Copy from an existing assessment' | translate }}</div>
                <div class="ra-radio-sub">{{ 'Pre-populate registers with qualitative scores, controls, notes, and other data from a previous assessment.' | translate }}</div>
              </div>
            </label>
          </div>
          @if (entity.initialQualitativeSelector === copyFromValue) {
            <div class="ra-clone-details">
              @if (entity.copyFromRiskAssessmentId) {
                <p class="ra-carry-forward-label">{{ 'Carry forward' | translate }}</p>
              }
              <div class="row">
                @for (field of cloneFields; track trackByIndex($index)) {
                  <app-dynamic-form-field [field]="field" [entity]="entity"
                  (valueChanged)="updateFieldVisibility()"></app-dynamic-form-field>
                }
              </div>
              @if (entity.copyFromRiskAssessmentId) {
                <div class="ra-warning-banner">
                  <i class="fa fa-exclamation-triangle"></i>
                  <span>{{ 'Quantitative data is not copied — it is recalculated automatically from samples within the new assessment\'s date range.' | translate }}</span>
                </div>
              }
            </div>
          }
        }

        <!-- ─── STEP: AGENTS ───────────────────────────────────────────── -->
        @if (currentStep?.id === 'agents') {
          @if (agentBannerIsError) {
            <div class="ra-filter-error" role="alert">{{ agentBannerText }}</div>
          } @else {
            <div class="ra-info-banner" role="note">
              <i class="fa fa-info-circle"></i>
              <span>{{ agentBannerText }}</span>
            </div>
          }
          <div class="row">
            @for (field of agentsFields; track trackByIndex($index)) {
              <app-dynamic-form-field [field]="field" [entity]="entity"
              (valueChanged)="updateFieldVisibility()"></app-dynamic-form-field>
            }
          </div>
        }

        <!-- ─── STEP: SEGS ─────────────────────────────────────────────── -->
        @if (currentStep?.id === 'segs') {
          @if (segBannerIsError) {
            <div class="ra-filter-error" role="alert">{{ segBannerText }}</div>
          } @else {
            <div class="ra-info-banner" role="note">
              <i class="fa fa-info-circle"></i>
              <span>{{ segBannerText }}</span>
            </div>
            @if (segBannerAdvisory) {
              <div class="ra-warning-banner" role="note">
                <i class="fa fa-exclamation-triangle"></i>
                <span>{{ segBannerAdvisory }}</span>
              </div>
            }
          }
          <div class="row">
            @for (field of segsFields; track trackByIndex($index)) {
              <app-dynamic-form-field [field]="field" [entity]="entity"
              (valueChanged)="updateFieldVisibility()"></app-dynamic-form-field>
            }
          </div>
        }

        <!-- ─── STEP: SAMPLE FILTERS (G19 / DASH-2976) ─────────────────── -->
        @if (currentStep?.id === 'sampleFilters') {
          <div class="ra-info-banner" role="note">
            <i class="fa fa-info-circle"></i>
            <span>{{ 'Sample filters scope which samples feed this assessment’s statistics. Every register is calculated only from the samples that match the scope below.' | translate }}</span>
          </div>
          <!-- Recalculation warning: filters stay editable after create and trigger a full recalculation (AC6). -->
          @if (!isAdd && !isApproved) {
            <div class="ra-warning-banner" role="alert">
              <i class="fa fa-exclamation-triangle"></i>
              <span>{{ 'Changing the sample filters recalculates the statistics for every register in this assessment. This is the only setting that can be changed after creation.' | translate }}</span>
            </div>
          }
          <!-- Read-only Personal-only baseline (AC5): Area and Blank samples are always excluded. -->
          <div class="ra-info-banner ra-baseline-note" role="note">
            <i class="fa fa-lock"></i>
            <span>{{ 'Only personal samples are included in this assessment. Area and blank samples are always excluded — this cannot be changed.' | translate }}</span>
          </div>
          <!-- Approved: static read-only summary rather than greyed controls (AC8). -->
          @if (isApproved) {
            <div class="ra-review-section">
              <p class="ra-review-title">{{ 'Sample scope' | translate }}</p>
              <p class="ra-review-note">{{ 'This assessment is approved, so its sample scope is locked and shown here for reference.' | translate }}</p>
              <div class="ra-review-table">
                @for (row of sampleFilterSummaryRows; track trackByIndex($index)) {
                  <ng-container
                    [ngTemplateOutlet]="reviewRowTpl"
                    [ngTemplateOutletContext]="{ label: row.label, value: row.value }">
                  </ng-container>
                }
              </div>
            </div>
          }
          <!-- Draft / InReview / Rejected: editable three-mode controls. -->
          @if (!isApproved) {
            <div class="ra-filter-dimensions">
              <app-sample-filter-dimension
                label="Shift Coverage"
                allLabel="All shift types"
                [options]="shiftCoverageOptions"
                [includeNone]="true"
                pickerPlaceholder="Select shift coverage"
                [value]="shiftCoverageFilter"
                (valueChange)="onShiftCoverageChange($event)"
              (validChange)="shiftCoverageValid = $event"></app-sample-filter-dimension>
              @if (!shiftCoverageValid) {
                <div class="ra-filter-error" role="alert">
                  {{ 'Select at least one value, and do not exclude every value — the same value cannot be both included and excluded.' | translate }}
                </div>
              }
              <app-sample-filter-dimension
                label="Sample Type"
                allLabel="All sample types"
                [options]="sampleTypeOptions"
                [includeNone]="false"
                pickerPlaceholder="Select sample types"
                [value]="sampleTypeFilter"
                (valueChange)="onSampleTypeChange($event)"
              (validChange)="sampleTypeValid = $event"></app-sample-filter-dimension>
              @if (!sampleTypeValid) {
                <div class="ra-filter-error" role="alert">
                  {{ 'Select at least one value, and do not exclude every value — the same value cannot be both included and excluded.' | translate }}
                </div>
              }
              <!-- Degraded state (AC7): a fresh tenant has no survey types, so only (None) can match. -->
              @if (hasNoSurveyTypes) {
                <div class="ra-info-banner ra-degraded-note" role="note">
                  <i class="fa fa-info-circle"></i>
                  <span>{{ 'No survey types are configured for this tenant yet, so samples can only be matched as unspecified survey type.' | translate }}</span>
                </div>
              }
              <app-sample-filter-dimension
                label="Survey Type"
                allLabel="All survey types"
                [options]="surveyTypeOptions"
                [includeNone]="true"
                pickerPlaceholder="Select survey types"
                [value]="surveyTypeFilter"
                (valueChange)="onSurveyTypeChange($event)"
              (validChange)="surveyTypeValid = $event"></app-sample-filter-dimension>
              @if (!surveyTypeValid) {
                <div class="ra-filter-error" role="alert">
                  {{ 'Select at least one value, and do not exclude every value — the same value cannot be both included and excluded.' | translate }}
                </div>
              }
            </div>
          }
        }

        <!-- ─── STEP 4: REVIEW ─────────────────────────────────────────── -->
        @if (currentStep?.id === 'review') {
          @if (isAdd) {
            <div class="ra-warning-banner ra-lock-warning" role="alert">
              <i class="fa fa-exclamation-triangle"></i>
              <span>{{ 'Please note — once the Risk Assessment is created, only the Name and the sample filters can be edited. Editing the sample filters recalculates the statistics for every register. Please review all other fields carefully before proceeding.' | translate }}</span>
            </div>
          }
          <div class="ra-review-section">
            <p class="ra-review-title">{{ 'Assessment summary' | translate }}</p>
            <div class="ra-review-table">
              @for (row of reviewRows; track trackByIndex($index)) {
                <ng-container
                  [ngTemplateOutlet]="reviewRowTpl"
                  [ngTemplateOutletContext]="{ label: row.label, value: row.value }">
                </ng-container>
              }
            </div>
          </div>
          <div class="ra-review-section">
            @if (oelGapLoading) {
              <div class="ra-oel-loading">
                <app-rings-loader></app-rings-loader>
                <span>{{ 'Checking OEL coverage…' | translate }}</span>
              </div>
            }
            @if (!oelGapLoading && oelGapAgentNames.length) {
              <div class="ra-warning-banner ra-warning-block" role="alert"
                >
                <i class="fa fa-exclamation-triangle"></i>
                <div class="ra-warning-body">
                  <div class="ra-warning-title">{{ 'Some agents have no OEL for the selected type' | translate }}</div>
                  <p>{{ 'The following agents in {{orgLevelName}} have no OEL defined for {{jurisdictionName}} / {{oelTypeName}}. Their registers will include any matching samples, but no statistics will be calculated and OEL will show as blank.' | translate: { orgLevelName: selectedOrgLevelName, jurisdictionName: selectedJurisdictionName, oelTypeName: selectedOelTypeName } }}</p>
                  <p class="mt-2">{{ 'This is not an error — you can still create the assessment. To get statistics for these agents, either add an OEL for {{oelTypeName}} in the Agents module, or change the primary OEL type to one these agents already have.' | translate: { oelTypeName: selectedOelTypeName } }}</p>
                  <ul class="ra-warning-agents">
                    @for (agentName of gapAgentsToShow; track trackByIndex($index)) {
                      <li>{{ agentName }}</li>
                    }
                  </ul>
                  @if (!showAllGapAgents && hiddenGapAgentCount > 0) {
                    <button type="button" class="ra-expand-link"
                      (click)="showAllGapAgents = true">
                      {{ '+{{count}} more' | translate: { count: hiddenGapAgentCount } }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        }

      </div>
    </ng-template>

    <ng-template #reviewRowTpl let-label="label" let-value="value">
      <div class="ra-review-row">
        <div class="ra-review-key">{{ label }}</div>
        <div class="ra-review-val">{{ value }}</div>
      </div>
    </ng-template>
